// Copied and modified: packages/components/account-subscriptions/billing-v2/refund/src/components/refund-page.tsx
import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { Refund } from '@atlassian/cssxp-types-billing';
import { AppContent, AppPage, ErrorComponents, withApollo, useFeatureToggle, useRecentlyViewed } from '@css-xp/base';
import { ViewPricing } from '@css-xp/base/common-sidebars';
import { fetchPartnerRefundDetailsQuery, getRefundSubheader } from '@css-xp/billing-v2';
import { StatelessInvoiceLineItems as RefundLineItem } from '@css-xp/billing-v2/refund/src/components/refund-line-items';
import RefundViewAdjustmentsPane from '@css-xp/billing-v2/refund/src/components/refund-view-adjustments-pane';
import {
  FetchPartnerRefundDetailQueryQuery,
  FetchPartnerRefundDetailQueryQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { Background } from '@css-xp/page-background/src/background-image';
import {
  PPC_PAGE_TITLE,
  routePaths,
  ENABLE_DESIGN_ITERATION_1,
  RecentlyViewedEntityEnum,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { getEntityName, getRouteUrl } from '@css-xp/ppc-utils';
import { RefundsPageLoader } from '@css-xp/ppc-page-loaders';
import { RefundDetailSummarySection, RefundDetailsPageActionsV2 } from './components/refund-details-summary-section';
import { RefundDetailsPageActions } from './components/refund-details-summary-section/refund-details-page-actions';
import { RefundInfoSection } from './components/refund-info-section';
import './refund-page.scss';

export enum SIDEBAR {
  VIEW_PRICING_DETAILS = 'viewPricingDetails',
  REFUND_VIEW_ADJUSTMENTS = 'refund-view-adjustments',
}

export const RefundsPage = (): JSX.Element | null => {
  const { refundId, txaId } = useParams<{ refundId: string; txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const isAddOnsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS);
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const history = useHistory();
  useTitle(refundId, PPC_PAGE_TITLE.INVOICES);
  const { updateRecentlyViewed } = useRecentlyViewed();

  const { data, loading, error } = useQuery<
    FetchPartnerRefundDetailQueryQuery,
    FetchPartnerRefundDetailQueryQueryVariables
  >(fetchPartnerRefundDetailsQuery, {
    variables: {
      id: refundId,
      transactionAccountId: txaId,
      ...(isAddOnsEnabled && { fetchAddOns: isAddOnsEnabled, fetchAGPAddon: isAddOnsEnabled }),
      fetchPricingPlan: true,
    },
  });

  useEffect(() => {
    const refund = ((data?.refund ? data.refund[0] : null) as unknown) as Refund;
    updateRecentlyViewed({
      entityName: getEntityName(RecentlyViewedEntityEnum.REFUND, txaId),
      metadata: {
        id: refundId,
        companyName: refund?.billTo?.fullName,
        refundNumber: refundId,
      },
    });
  }, [data]);

  if (loading) {
    if (isDesignIteration1Enabled) {
      return <RefundsPageLoader />;
    }

    return <LoadingIndicator />;
  }

  if (!data || !data?.refund) {
    // Redirect to not found page if no refund is found for the TXA
    history.push(routePaths.notFound);
  }

  if (error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);

    return <GenericError error={error} />;
  }

  const refund = ((data?.refund ? data.refund[0] : null) as unknown) as Refund;

  if (refund && !refund?.refundItems) {
    return null;
  }
  const subscriptionsRoutePPC = getRouteUrl(routePaths.subscriptions, { txaId: txaId || '' });

  const getPageHeading = () => {
    return (
      <div className="page-heading">
        <h1>{refundId}</h1>
        {getRefundSubheader(refund, true)}
      </div>
    );
  };

  const renderLineItems = (): JSX.Element | undefined => {
    return (
      <RefundLineItem
        refund={refund as Refund}
        entitlementBaseRoute={subscriptionsRoutePPC}
        isPaymentDetailsHidden={false}
        isInternalMemoHidden={true}
        shouldGroupAddOns={isAddOnsEnabled}
        isRefundSummaryHidden={isDesignIteration1Enabled}
      />
    );
  };

  const allowedSidebars = {
    [SIDEBAR.VIEW_PRICING_DETAILS]: withApollo(ViewPricing),
    [SIDEBAR.REFUND_VIEW_ADJUSTMENTS]: RefundViewAdjustmentsPane,
  };

  const creditNoteId = refund?.creditNoteId;

  const renderActions = () => {
    return <RefundDetailsPageActions creditNoteId={creditNoteId as string} transactionAccountId={txaId} />;
  };

  const renderInfoSection = () => {
    if (!isDesignIteration1Enabled || !refund) {
      return null;
    }

    return <RefundInfoSection refund={refund} txaId={txaId} />;
  };

  const renderAppPage = () => {
    return (
      <AppPage className="app-page-v2 ppc-refund-detail-page" allowedSidebars={allowedSidebars}>
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_REFUNDS_SCREEN,
            attributes: {},
          }}
        />
        <AppContent>
          {renderInfoSection()}
          <section>
            <RefundDetailSummarySection
              billingDoc={refund}
              transactionAccountId={txaId}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
            />
          </section>
          <div className="ppc-refund-details-lineitem">{renderLineItems()}</div>
        </AppContent>
      </AppPage>
    );
  };

  if (isDesignIteration1Enabled) {
    const breadcrumbsItems = [
      {
        title: 'Home',
        href: routePaths.home,
      },
      {
        title: 'Invoices',
        href: `${routePaths.invoices}#refunded`,
      },
      {
        title: refundId,
        href: routePaths.refundDetails,
      },
    ];

    const renderActionsV2 = () => {
      return <RefundDetailsPageActionsV2 creditNoteId={creditNoteId as string} transactionAccountId={txaId} />;
    };

    return (
      <PageBackgroundV2
        heading={getPageHeading()}
        className="ppc-refund-details-v2"
        breadcrumbsProps={{ items: breadcrumbsItems }}
        actions={renderActionsV2()}
      >
        {renderAppPage()}
      </PageBackgroundV2>
    );
  }

  return (
    <>
      <PageBackground
        heading={getPageHeading()}
        imageKey={Background.INVOICE_DETAILS_BACKGROUND}
        className="page-background ppc-refund-detail-background"
        isBackButtonEnabled={true}
        actions={renderActions()}
      >
        {renderAppPage()}
      </PageBackground>
    </>
  );
};
